import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { T } from "@/constants/testIds";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
    </svg>
  );
}

export default function ElderLogin() {
  const { user, login, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/cms" replace />;

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/cms";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res.otp_required) {
        setStep("otp");
        toast.info("A 6-digit verification code has been sent to your email.");
      } else {
        nav("/cms");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyOtp(email, code);
      nav("/cms");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-16" data-no-translate>
      <div className="w-full max-w-md paper-card p-10">
        <Link to="/" className="flex justify-center">
          <Logo />
        </Link>
        <p className="eyebrow text-gold-dark mt-4 text-center">Elder CMS</p>

        {step === "credentials" ? (
          <>
            <h1 className="heading-3 mt-6 text-center">Sign in to continue.</h1>
            <p className="body-copy mt-2 text-ink-muted text-sm text-center">
              For authors, editors, theological reviewers, translators, and administrators.
            </p>

            <button
              data-testid="google-signin-btn"
              type="button"
              onClick={googleSignIn}
              className="mt-8 w-full inline-flex items-center justify-center gap-3 border border-ink/20 bg-paper px-6 py-3 text-sm font-medium tracking-wide uppercase transition-[border-color,background-color] duration-300 hover:border-gold hover:bg-subtle"
              style={{ letterSpacing: "0.08em", borderRadius: "2px" }}
            >
              <GoogleIcon /> Sign in with Google
            </button>
            <p className="mt-2 text-[11px] text-ink-muted text-center">
              New here? Signing in with Google creates an Author account — you can write and submit
              articles for elder review. Publishing rights are granted by the administrator.
            </p>

            <div className="mt-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[11px] uppercase tracking-widest text-ink-muted">or with password</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={submitCredentials} className="mt-6 space-y-4">
              <div>
                <label className="eyebrow">Email</label>
                <input
                  data-testid={T.loginEmail}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
                />
              </div>
              <div>
                <label className="eyebrow">Password</label>
                <input
                  data-testid={T.loginPassword}
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
                />
              </div>
              <button data-testid={T.loginSubmit} disabled={busy} type="submit" className="btn-primary w-full justify-center">
                {busy ? "Checking…" : "Continue"}
              </button>
              <div className="text-center">
                <Link data-testid={T.loginForgot} to="/forgot-password" className="text-xs text-ink-muted hover:text-gold-dark">
                  Forgot password?
                </Link>
              </div>
            </form>
          </>
        ) : (
          <>
            <h1 className="heading-3 mt-6 text-center">Check your email.</h1>
            <p className="body-copy mt-2 text-ink-muted text-sm text-center">
              Enter the 6-digit verification code sent to <span className="text-ink">{email}</span>.
            </p>
            <form onSubmit={submitOtp} className="mt-8 space-y-4">
              <input
                data-testid="otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                className="w-full border border-border bg-paper px-4 py-4 text-center text-2xl tracking-[0.6em] font-serif focus:outline-none focus:border-gold"
              />
              <button
                data-testid="otp-submit"
                disabled={busy || code.length !== 6}
                type="submit"
                className="btn-gold w-full justify-center disabled:opacity-50 disabled:pointer-events-none"
              >
                {busy ? "Verifying…" : "Verify & Sign In"}
              </button>
              <button
                data-testid="otp-back"
                type="button"
                onClick={() => {
                  setStep("credentials");
                  setCode("");
                }}
                className="w-full text-xs text-ink-muted hover:text-ink text-center"
              >
                Use a different account
              </button>
            </form>
          </>
        )}

        <p className="mt-8 text-center text-xs text-ink-muted">
          Not an elder? <Link to="/" className="underline">Return home</Link>.
        </p>
      </div>
    </div>
  );
}
